import { supabase } from "./supabase";

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

/** An /admin API failure with a message fit to show staff. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /** The JSON error body, for routes that return more detail (e.g. a list of problems). */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const FALLBACK: Record<number, string> = {
  401: "Your session has ended. Sign in again.",
  403: "Your role cannot do this.",
  404: "Not found.",
  429: "Too many requests. Wait a minute and try again.",
  502: "The server could not finish this. Try again.",
  503: "The service is busy right now. Wait a minute and try again.",
  504: "The server took too long to respond. Try again.",
};

export interface AdminRequestOptions {
  /** Abort after this long (ms). Default 30 s. */
  timeoutMs?: number;
}

/** The last few API errors this session saw (status, path and message only), attached to support reports. */
export interface ApiErrorNote { status: number; path: string; message: string; at: string }
const recentErrors: ApiErrorNote[] = [];
export const recentApiErrors = (): ApiErrorNote[] => recentErrors.slice();
export function noteError(status: number, path: string, message: string) {
  recentErrors.push({ status, path: path.split(/[?#]/)[0]!, message: message.slice(0, 300), at: new Date().toISOString() });
  if (recentErrors.length > 10) recentErrors.shift();
}

/** A request to an API Worker route group (`/admin`, or a module's own group) with the current access token. */
export async function apiRequest(base: string, path: string, init: RequestInit, { timeoutMs = 30_000 }: AdminRequestOptions): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    // Multipart uploads (FormData) set their own content type with the boundary.
    const json = init.body !== undefined && !(init.body instanceof FormData);
    res = await fetch(`${API_URL}${base}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: { ...(json ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}), ...init.headers },
    });
  } catch {
    const err = ctrl.signal.aborted ? new ApiError(0, "This is taking too long. Try again in a minute.", "timeout") : new ApiError(0, "Could not reach the server. Check your connection and try again.", "network");
    noteError(0, `${base}${path}`, err.message);
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    let body: { error?: string; message?: string; [k: string]: unknown } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      /* not JSON */
    }
    const message = body.message ?? FALLBACK[res.status] ?? `Request failed (${res.status}).`;
    noteError(res.status, `${base}${path}`, message);
    throw new ApiError(res.status, message, body.error, body);
  }
  return res;
}

/** No signal, or the request gave up: the cases where a saved copy is shown or an action is saved for later. */
export const isNetworkError = (e: unknown) => e instanceof ApiError && (e.code === "network" || e.code === "timeout");

/** Thrown when an action was saved on the phone to send later. Show it as done, not as an error. */
export const QUEUED_MESSAGE = "Saved on this phone. It will send when you're back online.";
export const isQueued = (e: unknown) => e instanceof ApiError && e.code === "queued";

/** Call an /admin route on the API Worker with the current Supabase access token. */
export async function adminFetch<T>(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<T> {
  return (await (await apiRequest("/admin", path, init, opts)).json()) as T;
}

/** Fetch a binary /admin resource (for example a PDF). */
export async function adminBlob(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<Blob> {
  return (await apiRequest("/admin", path, init, opts)).blob();
}

/** Call a /help route (help documents and support reports, for every signed-in user). */
export async function helpFetch<T>(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<T> {
  return (await (await apiRequest("/help", path, init, opts)).json()) as T;
}

/** Fetch a binary /help resource (a help document or a report's screenshot). */
export async function helpBlob(path: string, opts: AdminRequestOptions = {}): Promise<Blob> {
  return (await apiRequest("/help", path, {}, opts)).blob();
}
